import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Box, Button, Card, Flex, Heading, Text } from 'rebass';
import { compose, withApollo } from 'react-apollo';
import gql from 'graphql-tag';
import * as Yup from 'yup';

import { Input, InputWithLabel, TextInput, TextInputWithLabel } from '../../components/TextInput';
import PlacesAutocomplete from '../../components/addressAutoComplete/PlacesAutocomplete';
import { withToastManager } from 'react-toast-notifications';
import RegionSelect, { STATES } from '../../components/RegionSelect';
import theme from '../../styles/theme';
import ReactInputMask from 'react-input-mask';
import Select from 'react-select';
import { isMobile } from 'react-device-detect';

const customStyles = {
  control: (base, state) => ({
    ...base,
    border: 'none', // Removes the default border
    boxShadow: 'none', // Removes the default box shadow
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: 0, // Remove padding from the value container
  }),
  indicatorsContainer: (provided, state) => ({
    ...provided,
    display: 'none',
  }),
};

const phoneRegExp = /^((\+1|1)?( |-)?)?(\([2-9][0-9]{2}\)|[2-9][0-9]{2})( |-)?([2-9][0-9]{2}( |-)?[0-9]{4})$/;

const PHONE_AVAILABLE = gql`
  query PhoneAvailable($phone: String!) {
    phoneAvailable(phone: $phone) {
      ok
    }
  }
`;

const schema = Yup.object().shape({
  firstName: Yup.string()
    .trim()
    .min(2)
    .required('Required'),
  lastName: Yup.string()
    .trim()
    .min(2)
    .required('Required'),
  street1: Yup.string().required('Required'),
  city: Yup.string().required('Required'),
  state: Yup.string().required('Required'),
  zip: Yup.string().required('Required'),
  phone: Yup.string()
    .trim()
    .required('Required')
    .matches(phoneRegExp, 'Phone number is not valid'),
});

const Profile = ({ state, onChange, onContinue, enforceDepartment, toastManager, onChangeMultiple, client }) => {
  const [forceHideAutocomplete, setForceHideAutocomplete] = useState(false);
  const [isFormExtended, setIsFormExtended] = useState();
  const [isChecking, setIsChecking] = useState();
  const [isValid, setIsValid] = useState();
  const phoneRef = useRef();
  const zipRef = useRef();
  const cityRef = useRef();
  const street2Ref = useRef();
  const streetRef = useRef();
  const lastNameRef = useRef();
  const firstNameRef = useRef();
  const selectRef = useRef();
  const handleSelectClick = () => {
    if (selectRef.current) {
      selectRef.current.focus();
    }
  };
  const handleFirstNameClick = () => {
    if (firstNameRef.current) {
      firstNameRef.current.focus();
    }
  };
  const handleLastNameClick = () => {
    if (lastNameRef.current) {
      lastNameRef.current.focus();
    }
  };
  const handleStreetClick = () => {
    if (streetRef.current) {
      streetRef.current.focus();
    }
  };
  const handleStreet2Click = () => {
    if (street2Ref.current) {
      street2Ref.current.focus();
    }
  };
  const handleCityClick = () => {
    if (cityRef.current) {
      cityRef.current.focus();
    }
  };
  const handleZipClick = () => {
    if (zipRef.current) {
      zipRef.current.focus();
    }
  };
  const handlePhoneClick = () => {
    if (phoneRef.current) {
      phoneRef.current.focus();
    }
  };

  useEffect(() => {
    const hasAddress = state && state.street1 && state.street1.length > 0;
    setIsFormExtended(hasAddress);
  }, []);

  useEffect(
    () => {
      schema
        .validate(state)
        .then((valid) => {
          setIsValid(true);
        })
        .catch((error) => {
          console.log(error);
          setIsValid(false);
        });
    },
    [state]
  );

  const submitCheck = async () => {
    setIsChecking(true);
    if (enforceDepartment) {
      if (state.state !== enforceDepartment) {
        toastManager.add('Your address state differs from the state you selected in prequalification', {
          appearance: 'error',
          autoDismiss: true,
        });
        setIsChecking(false);
        return;
      }
    }
    try {
      const { data } = await client.query({
        query: PHONE_AVAILABLE,
        variables: {
          phone: state.phone,
        },
      });

      setIsChecking(false);

      if (!data.phoneAvailable.ok) {
        throw Error('Number already in use');
      } else {
        onContinue();
      }
    } catch (error) {
      setIsChecking(false);
      toastManager.add(error.message, {
        appearance: 'error',
        autoDismiss: true,
      });
    }
  };

  return (
    <Flex mb={4} flex={1} flexDirection={['column', 'column', 'row']}>
      <Flex flex={1} mb={[4, 4, 0]}>
        <Flex flexDirection="column" flex={1}>
          <Text style={{ fontSize: isMobile ? '26px' : '32px', fontWeight: 500 }} mb={2}>
            Set Up Your Profile
          </Text>
          <Text style={{ fontSize: isMobile ? '16px' : '18px' }} mb={isMobile ? 4 : 5}>
            Let's get started with some basic information.
          </Text>

          <Fragment>
            <Flex flexDirection={['column', 'column', 'row']} mb={3}>
              <Flex
                mr={[0, 0, 3]}
                mb={[3, 3, 0]}
                flex={1}
                flexDirection={'row'}
                onClick={handleFirstNameClick}
                style={{ border: `1px solid #909ebb`, borderRadius: '12px', padding: '12px 0px 0px 0px', background: 'white', cursor: 'pointer', alignItems: 'center' }}
              >
                <Flex flex={1} flexDirection={'column'}>
                  <InputWithLabel
                    style={{ border: 0, background: 'white', color: '#344f79', fontSize: isMobile ? '14px' : '' }}
                    subref={firstNameRef}
                    placeholder="First Name"
                    type="text"
                    name="firstName"
                    id="input_firstname"
                    value={state['firstName']}
                    onChange={(e) => {
                      onChange('firstName', e.target.value);
                    }}
                  />
                </Flex>
              </Flex>
              <Flex
                flex={1}
                flexDirection={'row'}
                onClick={handleLastNameClick}
                style={{ border: `1px solid #909ebb`, borderRadius: '12px', padding: '12px 0px 0px 0px', background: 'white', cursor: 'pointer', alignItems: 'center' }}
              >
                <Flex flex={1} flexDirection={'column'}>
                  <InputWithLabel
                    style={{ border: 0, background: 'white', color: '#344f79', fontSize: isMobile ? '14px' : '' }}
                    subref={lastNameRef}
                    placeholder="Last Name"
                    type="text"
                    name="lastName"
                    id="input_lastname"
                    value={state['lastName']}
                    onChange={(e) => {
                      onChange('lastName', e.target.value);
                    }}
                  />
                </Flex>
              </Flex>
            </Flex>

            <Box
              mb={3}
              style={{
                flex: 1,
                width: '100%',
                maxHeight: state.street1 || state.city || state.zip ? '0px' : '',
                opacity: state.street1 || state.city || state.zip ? '0' : '1',
              }}
            >
              {/* <Flex mb={3} flex={1} id='pac'> */}
              <PlacesAutocomplete
                unfield
                fieldname="address"
                onTextChanged={(val) => {
                  if (val && val.length > 0) {
                    if (!isFormExtended) {
                      setIsFormExtended(true);
                    }
                  } else {
                    if (!state.street1 && !state.street2 && !state.city && !state.zip) {
                      setIsFormExtended(false);
                    }
                  }
                }}
                onSelected={(val) => {
                  onChangeMultiple([{ id: 'street1', value: val.street }, { id: 'city', value: val.city }, { id: 'zip', value: val.zip }, { id: 'state', value: val.state }]);
                }}
                onBlur={(val) => {
                  // console.log('OnBlur');
                  if (!isFormExtended) {
                    setForceHideAutocomplete(true);
                    setIsFormExtended(true);
                  }
                  if (!state.street1 && !state.street2 && !state.city && !state.zip) {
                    onChangeMultiple([{ id: 'street1', value: null }, { id: 'city', value: null }, { id: 'zip', value: null }, { id: 'state', value: null }]);
                  }
                }}
                inputStyle={{
                  border: `1px solid #909ebb`,
                  borderRadius: '12px',
                  fontSize: isMobile ? '14px' : '',
                }}
                containerStyle={{ width: '100%' }}
                street1FieldName="street1"
                cityFieldName="city"
                zipFieldName="zip"
                stateFieldName="state"
              />
              {/* </Flex> */}
            </Box>
            {/* )} */}
            {isFormExtended && (
              <>
                <Flex
                  flex={1}
                  mb={3}
                  flexDirection={'row'}
                  onClick={handleStreetClick}
                  style={{ border: `1px solid #909ebb`, borderRadius: '12px', padding: '12px 0px 0px 0px', background: 'white', cursor: 'pointer', alignItems: 'center' }}
                >
                  <Flex flex={1} flexDirection={'column'}>
                    <InputWithLabel
                      style={{ border: 0, background: 'white', color: '#344f79', fontSize: isMobile ? '14px' : '' }}
                      subref={streetRef}
                      placeholder="Address"
                      type="text"
                      name="street1"
                      value={state['street1']}
                      onChange={(e) => {
                        onChange('street1', e.target.value);
                      }}
                    />
                  </Flex>
                </Flex>
                <Flex
                  flex={1}
                  mb={3}
                  flexDirection={'row'}
                  onClick={handleStreet2Click}
                  style={{ border: `1px solid #909ebb`, borderRadius: '12px', padding: '12px 0px 0px 0px', background: 'white', cursor: 'pointer', alignItems: 'center' }}
                >
                  <Flex flex={1} flexDirection={'column'}>
                    <InputWithLabel
                      style={{ border: 0, background: 'white', color: '#344f79', fontSize: isMobile ? '14px' : '' }}
                      subref={street2Ref}
                      placeholder="Apartment, Suite, Floor (Optional)"
                      type="text"
                      name="street2"
                      value={state['street2']}
                      onChange={(e) => {
                        onChange('street2', e.target.value);
                      }}
                    />
                  </Flex>
                </Flex>

                <Flex flexDirection={['column', 'column', 'row']}>
                  <Flex width={[1, 1, 1 / 3]} mr={[0, 0, 3]} mb={3}>
                    <Flex
                      flex={1}
                      flexDirection={'row'}
                      onClick={handleCityClick}
                      style={{ border: `1px solid #909ebb`, borderRadius: '12px', padding: '12px 0px 0px 0px', background: 'white', cursor: 'pointer', alignItems: 'center' }}
                    >
                      <Flex flex={1} flexDirection={'column'}>
                        <InputWithLabel
                          style={{ border: 0, background: 'white', color: '#344f79', fontSize: isMobile ? '14px' : '' }}
                          subref={cityRef}
                          placeholder="City"
                          type="text"
                          name="city"
                          value={state['city']}
                          onChange={(e) => {
                            onChange('city', e.target.value);
                          }}
                        />
                      </Flex>
                    </Flex>
                  </Flex>

                  <Flex width={[1, 1, 1 / 3]} mr={[0, 0, 3]} mb={3}>
                    <Flex
                      flex={1}
                      flexDirection={'row'}
                      onClick={handleSelectClick}
                      style={{ border: `1px solid #909ebb`, borderRadius: '12px', padding: '12px 16px', background: 'white', cursor: 'pointer', alignItems: 'center' }}
                    >
                      <Flex flex={1} flexDirection={'column'}>
                        <Text mb={0} style={{ opacity: 0.7 }} fontSize={'13px'} fontWeight={500}>
                          State
                        </Text>

                        <Select
                          ref={selectRef}
                          options={STATES}
                          value={STATES.filter((element) => element.value === state['state'])}
                          onChange={(option) => onChange('state', option.value)}
                          openMenuOnFocus={true}
                          styles={customStyles}
                        />
                      </Flex>

                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M5.83984 9.59001L11.4998 15.25L17.1598 9.59001L16.4498 8.89001L11.4998 13.84L6.54984 8.89001L5.83984 9.59001Z" fill="#7992B3" />
                      </svg>
                    </Flex>
                    {/* <RegionSelect name="state" value={values.state} /> */}
                  </Flex>
                  <Flex width={[1, 1, 1 / 3]} mb={3}>
                    <Flex
                      flex={1}
                      flexDirection={'row'}
                      onClick={handleZipClick}
                      style={{ border: `1px solid #909ebb`, borderRadius: '12px', padding: '12px 0px 0px 0px', background: 'white', cursor: 'pointer', alignItems: 'center' }}
                    >
                      <Flex flex={1} flexDirection={'column'}>
                        <InputWithLabel
                          style={{ border: 0, background: 'white', color: '#344f79', fontSize: isMobile ? '14px' : '' }}
                          subref={zipRef}
                          placeholder="Zip"
                          type="text"
                          name="zip"
                          value={state['zip']}
                          onChange={(e) => {
                            onChange('zip', e.target.value);
                          }}
                        />
                      </Flex>
                    </Flex>
                  </Flex>
                </Flex>
              </>
            )}
            <Flex mt={3} mb={4}>
              <Flex
                flex={1}
                flexDirection={'row'}
                onClick={handlePhoneClick}
                style={{ border: `1px solid #909ebb`, borderRadius: '12px', padding: '12px 16px', background: 'white', cursor: 'pointer', alignItems: 'center' }}
              >
                <Flex flex={1} flexDirection={'column'}>
                  <ReactInputMask
                    name="phone"
                    placeholder="Mobile Phone Number"
                    mask="999-999-9999"
                    value={state['phone']}
                    onChange={(e) => {
                      onChangeMultiple([{ id: 'phone', value: e.target.value }, { id: 'mfa', value: null }]);
                    }}
                    id="input_phone"
                  >
                    {(inputProps) => <Input style={{ border: 0, background: 'white', color: '#344f79', padding: '16px 0' }} subref={phoneRef} {...inputProps} />}
                  </ReactInputMask>

                  <Box mb={0} mt={2}>
                    <Text>
                      By entering your phone number, you agree to receive text messages from Paloma Health about your account, reminders, or special offers. Message and data rates may apply. You can
                      opt out anytime by replying STOP. Consent is not a condition of purchase.
                    </Text>
                  </Box>
                </Flex>
              </Flex>

              {/* <MaskInput name="phone" placeholder="Mobile Phone Number" mask="999-999-9999" value={values.phone} onChange={handleChange} onBlur={handleBlur} id="input_phone" /> */}
            </Flex>

            <Flex justifyContent="center">
              <Button
                disabled={isChecking || !isValid || !state || !state.street1 || state.street1.indexOf('@') > -1}
                variant="primary"
                width={[1 / 2, 1 / 3]}
                type="submit"
                id="btn_next"
                onClick={submitCheck}
              >
                Next
              </Button>
            </Flex>
          </Fragment>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default compose(
  withApollo,
  withToastManager
)(Profile);
